import React, { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { useDispatch, useSelector, shallowEqual, useStore } from "react-redux";
import AppHeader from "../../components/header";
import Topbar from "../../components/sections/DetailsTopBar/DetailsTopBar";
import DetailsContent from "../../components/sections/DetailsContent/DetailsContent";
import DetailImages from "../../components/sections/DetailsImage/DetailsImage";
import DetailCard from "../../components/sections/DetailsCard/DetailsCard";
import RoomDetails from "../../components/sections/RoomCardSlider/RoomCardSlider";
import FlightDetails from "../../components/sections/FlightSlider/FlightSlider";
// import TextSection from "../../components/sections/BottomTextSetion/BottomTextSetion";
import Footer from "../../components/footer";
import { ProgressBar, Step } from "react-step-progress-bar";
import "./style.scss";
import RoomIcon from "../../assets/images/bt-room.svg";
import FlightIcon from "../../assets/images/icons/flight.svg";
import ConfirmIcon from "../../assets/images/bt-confirm.svg";
import {
    getAppConfig,
    setActivityIndicator,
} from "../../redux/slices/appconfigSlice";
import {
    GetFlightDetailsService,
    GetHotelDetailsService,
} from "../../services/HotelDetailServices";
import {
    getSearchFilters,
    setSelectedHotel,
    setSelectedHotelDetail,
    setRoomFlights,
    setFlightCabinClass,
    setPaginatedRoomTypes,
    setPaginatedRoomFlights,
} from "../../redux/slices/searchFilterSlice";
import {
    setHotelBooking,
    setFlight,
    setHotelRoom,
    resetBookingState,
} from "../../redux/slices/bookingSlice";
import {
    calculateRoomsUpSell, getFlightObjectFromGPTOffer,
    getFlightOfferFromAppConfigOffers,
    getFlightsParams, getTravelerCountFromRoomUrl, replaceQueryParam,
} from "../../helpers/common";
import { getLoginConfig } from "../../redux/slices/loginconfigSlice";
import { useNavigate } from "react-router-dom";
import useTranslationCustom from "../../i18n/customHook/useTranslation";
import { buildShareLink, getFlightNumbersString } from "../../utils/webShareLink";
import { captureDetailUrl, endDetailSession, getDetailUrl, isDetailSessionLive } from "../../utils/detailUrl";

const Deatils = () => {
    const { T } = useTranslationCustom();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const store = useStore();

    // single searchFilters subscription (shallowEqual)
    const searchfilters = useSelector(getSearchFilters, shallowEqual);
    const { selectedHotel, selectedHotelToShare } = searchfilters || {};

    const loginConfig = useSelector(getLoginConfig, shallowEqual);
    const appConfig = useSelector(getAppConfig, shallowEqual);

    // select only what we need from booking slice to avoid re-renders from unrelated booking changes
    const room = useSelector(state => state.booking.room);
    const flight = useSelector(state => state.booking.flight);

    // The readable detail URL. navigateToDetailPage already captured it; this
    // covers a direct load / refresh that never went through it. Lazy useState
    // runs once during the first render — before any effect, so it always lands
    // ahead of the address bar being swapped for the share URL below.
    useState(() => {
        if (!isDetailSessionLive() && typeof window !== 'undefined') {
            captureDetailUrl(window.location.href);
        }
    });
    const url = getDetailUrl();

    const hotelId = useMemo(() => {
        try {
            return new URL(url).searchParams.get("hotelId");
        } catch (e) {
            return null;
        }
    }, [url]);

    const isFav = typeof window !== 'undefined' ? new URL(url).searchParams.get("fav") : null;
    const isChatOffer = new URL(url).searchParams.get("chatOffer") === 'true';
    const flightNumbersStringFromUrl = new URL(url).searchParams.get("flightNumbersString");
    // const isMapOffer = new URL(url).searchParams.get("mapOffer") === 'true';
    const prevUrl = localStorage.getItem('prevUrl');

    // refs
    const bookingCardMobile = useRef(null);
    const bookingCardDesktop = useRef(null);
    const amenityHeading = useRef(null);
    const flightRef = useRef(null);
    const headerRef = useRef(null);

    // Last share URL written to the address bar. Null until the first successful
    // package details response, which is what gates the swap; afterwards it also
    // dedupes, so an unchanged link is never rewritten.
    const shareUrlRef = useRef(null);

    const [justArrived, setJustArrived] = useState(true);
    const [roomsFetched, setRoomsFetched] = useState(false);
    const [flightsFetched, setFlightsFetched] = useState(false);
    const [amenities, setAmenities] = useState([]);
    const [showMore, setShowMore] = useState(false);

    const [steps, setSteps] = useState(() => [
        { name: T("Packages_Detail:Floater_Choose_Room"), completed: false, icon: RoomIcon },
        { name: T("Packages_Detail:Floater_Choose_Flight"), completed: false, icon: FlightIcon },
        { name: T("Packages_Detail:Floater_Confirm_Booking"), completed: false, icon: ConfirmIcon },
    ]);

    const OFFER_ANIMATION_PLAYED_KEY = "hotelDetailAnimationPlayed";
    const LEGACY_OFFER_ANIMATION_KEY_PREFIX = `${OFFER_ANIMATION_PLAYED_KEY}:`;

    // recompute query params from the canonical URL on every call (prevents stale
    // searchParams — roomKey / offerId writes update the canonical URL in place)
    const getQueryParamsFromURL = () => {
        const currentUrl = getDetailUrl();
        const objUrl = new URL(currentUrl);
        const sp = new URLSearchParams(objUrl.search);

        const params = {
            upSell: false,
            Adults: Number(sp.get("Adults")),
            Children: Number(sp.get("Children")),
            Infants: Number(sp.get("Infants")),
            Locale: sp.get("Locale"),
            fav: sp.get("fav"),
            roomKey: sp.get("roomKey"),
            GUID: sp.get("GUID"),
            NumberOfStops: sp.get("NumberOfStops"),
            Departure: sp.get("Departure"),
            Destination: sp.get("Destination"),
            DepartureDate: sp.get("DepartureDate"),
            ReturnDate: sp.get("ReturnDate"),
            apiDetailsUri: sp.get("apiDetailsUri"),
        };

        // const isPackageGuid = () => !!appConfig?.packageTypes.find(f => f.guid === params.GUID);

        if (sp.get("flightCabinClass") && !isChatOffer) {
            params["flightCabinClass"] = searchfilters?.flightCabinClass;
        }

        return params;
    };

    // Point the address bar at the share URL for the package currently on screen.
    // history.replaceState only: no reload, no router navigation, no React state,
    // so nothing re-renders and the router keeps rendering this page.
    const syncShareUrlToAddressBar = () => {
        try {
            // Read the flight from the store rather than the closure: this runs
            // straight after dispatches whose values the closure has not seen yet.
            const shareFlight = store.getState().booking.flight;
            const shareUrl = buildShareLink(selectedHotelToShare, shareFlight);
            if (!shareUrl || shareUrl === shareUrlRef.current) {
                return;
            }
            shareUrlRef.current = shareUrl;
            window.history.replaceState(window.history.state, "", shareUrl);
        } catch (e) {
            console.error("[ShareURL] failed to replace address bar:", e);
        }
    };

    // Keep the address bar in step with the selected flight. The share token
    // encodes the itinerary (airlineCode / flightNumbersString), so swiping the
    // flight slider changes it — without this, the URL a user copies from the bar
    // would point at the flight they started on, not the one they are looking at.
    //
    // Both deps are needed: offerId catches a different flight being selected,
    // flightsFetched catches the same flight arriving with its full segments/legs
    // from getMoreFlights (same offerId, richer object, different token).
    // Gated on shareUrlRef so it never runs before the first successful response;
    // syncShareUrlToAddressBar no-ops when the rebuilt link is unchanged.
    useEffect(() => {
        if (shareUrlRef.current) {
            syncShareUrlToAddressBar();
        }
    }, [flight?.offerId, flightsFetched]); // eslint-disable-line

    const clearLegacyOfferAnimationRecords = () => {
        Object.keys(localStorage).forEach((key) => {
            if (key.startsWith(LEGACY_OFFER_ANIMATION_KEY_PREFIX)) {
                localStorage.removeItem(key);
            }
        });
    };

    const saveOfferAnimationPlayed = () => {
        localStorage.setItem(OFFER_ANIMATION_PLAYED_KEY, "true");
        clearLegacyOfferAnimationRecords();
    };

    const hasOfferAnimationPlayed = () => {
        if (localStorage.getItem(OFFER_ANIMATION_PLAYED_KEY) === "true") {
            return true;
        }

        const hasLegacyAnimationRecord = Object.keys(localStorage).some((key) =>
            key.startsWith(LEGACY_OFFER_ANIMATION_KEY_PREFIX)
        );

        if (hasLegacyAnimationRecord) {
            saveOfferAnimationPlayed();
        }

        return hasLegacyAnimationRecord;
    };

    // memoize images (you already did this — kept)
    const images = React.useMemo(() => {
        const media = searchfilters?.selectedHotel?.media;
        if (!media) return [];
        return media.map(obj => ({ url: obj?.url }));
    }, [searchfilters?.selectedHotel?.media]);

    const resetDetailStates = () => {
        dispatch(resetBookingState());
        dispatch(setSelectedHotel(null));
        dispatch(setSelectedHotelDetail([]));
        dispatch(setPaginatedRoomTypes([]));
        dispatch(setRoomFlights([]));
        dispatch(setPaginatedRoomFlights([]));
    }

    let scrollToDetail = null;
    let scrollToHeader = null;
    let scrollToCombinedDetails = null;
    let scrollAnimationId = null;
    let isScrolling = false;

    // Helper function for iOS-compatible scroll to top
    const scrollToTop = () => {
        const isIosSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) &&
            !window.MSStream;

        if (isIosSafari) {
            // iOS Safari: use multiple scroll targets
            const scrollEl = document.scrollingElement || document.documentElement || document.body;
            scrollEl.scrollTop = 0;
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
            window.scrollTo(0, 0);
        } else {
            window.scrollTo(0, 0);
        }
    };

    /**
     * Silky-smooth scroll with ZERO jerk.
     * - Target is locked once so React re-renders can't jitter the animation.
     * - Sine ease-in-out gives the most natural, TV-like glide.
     * - Duration defaults to 3.5s.
     */
    const smoothScrollTo = (element, duration = 3500) => {
        if (!element) return;

        // Wait for any ongoing scroll to finish
        if (isScrolling) {
            setTimeout(() => smoothScrollTo(element, duration), 100);
            return;
        }

        if (scrollAnimationId) {
            cancelAnimationFrame(scrollAnimationId);
            scrollAnimationId = null;
        }

        isScrolling = true;

        // Force multiple layout reads for iOS
        void document.body.offsetHeight;
        void document.documentElement.scrollTop;

        const startY = window.pageYOffset || window.scrollY || document.documentElement.scrollTop;
        const targetY = element.getBoundingClientRect().top + startY;
        const distance = targetY - startY;

        if (Math.abs(distance) < 2) {
            isScrolling = false;
            return;
        }

        // Detect iOS (all browsers on iOS use WebKit)
        const isIOSDevice = /iPhone|iPod|iPad/.test(navigator.userAgent) ||
            (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

        if (isIOSDevice) {
            // iOS: defer to next frames so layout settles after render
            let attempts = 0;
            const startAnimation = () => {
                const scrollEl = document.scrollingElement || document.documentElement || document.body;
                const iosStartY = window.pageYOffset || window.scrollY || scrollEl.scrollTop || 0;
                const rect = element.getBoundingClientRect();

                if (!rect || (rect.top === 0 && rect.bottom === 0 && element.scrollHeight === 0)) {
                    if (attempts < 10) {
                        attempts += 1;
                        setTimeout(startAnimation, 150);
                        return;
                    }
                    isScrolling = false;
                    return;
                }

                const iosTargetY = rect.top + iosStartY;
                const iosDistance = iosTargetY - iosStartY;

                if (Math.abs(iosDistance) < 2) {
                    if (attempts < 3) {
                        attempts += 1;
                        setTimeout(startAnimation, 150);
                        return;
                    }
                    isScrolling = false;
                    return;
                }

                let startTime = null;
                const easeInOutSine = (t) => -(Math.cos(Math.PI * t) - 1) / 2;

                const step = (timestamp) => {
                    if (!startTime) startTime = timestamp;
                    const progress = Math.min((timestamp - startTime) / duration, 1);
                    const nextY = iosStartY + iosDistance * easeInOutSine(progress);

                    scrollEl.scrollTop = nextY;
                    document.documentElement.scrollTop = nextY;
                    document.body.scrollTop = nextY;
                    window.scrollTo(0, nextY);

                    if (progress < 1) {
                        scrollAnimationId = requestAnimationFrame(step);
                    } else {
                        isScrolling = false;
                    }
                };

                scrollAnimationId = requestAnimationFrame(step);
            };

            requestAnimationFrame(() => requestAnimationFrame(startAnimation));
            setTimeout(startAnimation, 0);
        } else {
            // Desktop and Android: Original working logic
            let startTime = null;
            const easeInOutSine = (t) => -(Math.cos(Math.PI * t) - 1) / 2;

            const step = (timestamp) => {
                if (!startTime) startTime = timestamp;
                const progress = Math.min((timestamp - startTime) / duration, 1);

                window.scrollTo({
                    top: startY + distance * easeInOutSine(progress),
                    behavior: 'instant',   // bypass browser smoothing — we handle it
                });

                if (progress < 1) {
                    scrollAnimationId = requestAnimationFrame(step);
                } else {
                    isScrolling = false;
                }
            };

            scrollAnimationId = requestAnimationFrame(step);
        }
    };

    useEffect(() => {
        const params = getQueryParamsFromURL();
        scrollToTop();
        if (!room && !flight) {
            localStorage.setItem('hotelDetailPageFocus', 'true');
            GetPackageDetailUpSellFalse(params);
        }

        if (hotelId !== searchfilters?.selectedHotel?.id) {
            resetDetailStates()
        } else {
            const _roomFetched = localStorage.getItem('roomFetched');
            const _flightFetched = localStorage.getItem('flightFetched');

            setRoomsFetched(_roomFetched === 'true');
            setFlightsFetched(_flightFetched === 'true');
        }

        const timerStepper = setTimeout(() => {
            setSteps(prev => {
                if (prev[0]?.completed && prev[1]?.completed) return prev;
                return prev.map((step, index) =>
                    index === 0 || index === 1 ? { ...step, completed: true } : step
                );
            });
        }, 1000);

        return () => {
            resetDetailStates();
            // Ends this detail session, so /link goes back to being owned by Home
            // (see src/routes/index.js). The URL itself is kept: pages opened from
            // here (gallery, map) navigate back to it.
            endDetailSession();
            clearTimeout(scrollToDetail);
            clearTimeout(scrollToHeader);
            clearTimeout(scrollToCombinedDetails);
            clearTimeout(timerStepper);
            cancelAnimationFrame(scrollAnimationId);
            localStorage.setItem('hotelDetailPageFocus', 'false');
            localStorage.setItem('roomFetched', 'true');
            localStorage.setItem('flightFetched', 'true');
        };
    }, []); // eslint-disable-line

    useEffect(() => {
        if (!justArrived) {
            if (getQueryParamsFromURL()?.fav === "false") {
                navigate("/", { replace: true });
            }
        } else {
            setJustArrived(false);
        }
    }, [loginConfig.locale, loginConfig.currency]); // these were the original deps

    useEffect(() => {
        if (selectedHotel?.hotelAmenities?.length) {
            if (showMore) {
                setAmenities(selectedHotel?.hotelAmenities);
            } else {
                setAmenities(selectedHotel?.hotelAmenities?.slice(0, 9));
            }
        }
    }, [selectedHotel, showMore]);

    function calculatePercentage() {
        if (steps[2]?.completed) {
            return 100;
        } else if (steps[1]?.completed) {
            return 50;
        } else if (steps[0]?.completed) {
            return 0;
        } else return 0;
    }

    // const getFlightObjectFromGPTOffer = () => {
    //     const _offers = appConfig.chatGPTOffers;
    //     const hotel = _offers?.find(f => f.guidForBooking === getQueryParamsFromURL().GUID);
    //     return hotel?.objHotel?.newPackagedOffer?.flightOffer || null
    // }

    // Use store.getState() when you need the whole booking object inside async logic
    const GetPackageDetailUpSellFalse = (params) => {
        delete params.fav;
        delete params.chatOffer;
        dispatch(setActivityIndicator(true));
        GetHotelDetailsService(params)
            .then((_hotelDetails) => {
                dispatch(setActivityIndicator(false));

                const combinedAmenities = [
                    ..._hotelDetails?.packageDetails?.hotelOffer?.hotels[0]?.hotelAmenities || [],
                    ..._hotelDetails?.packageDetails?.hotelOffer?.hotels[0]?.roomAmenities || [],
                ];

                const roomsTypes =
                    _hotelDetails?.packageDetails?.hotelOffer?.hotels[0]?.roomTypes;
                const hotel = _hotelDetails?.packageDetails?.hotelOffer?.hotels[0];

                const flightOffer = isFav === 'true' ?
                    {
                        price: _hotelDetails?.price,
                        links: hotel.roomTypes[0].links,
                        surcharges: 0,
                        ...searchfilters.selectedFavoriteHotel?.objHotel?.newPackagedOffer?.flightOffer
                    } : isChatOffer ? getFlightObjectFromGPTOffer(appConfig, getQueryParamsFromURL().GUID, hotelId) :
                        getFlightOfferFromAppConfigOffers(appConfig, hotelId);

                const totalTravelers = getTravelerCountFromRoomUrl(roomsTypes[0]?.links?.webDetails?.href);

                const dataToAdd = {
                    ...hotel,
                    price: {
                        totalPrice: _hotelDetails?.price?.packageTotalPrice?.value,
                        currency: _hotelDetails?.price?.packageTotalPrice?.currency,
                    },
                    perPersonPrice: {
                        totalPrice: _hotelDetails?.price?.averageCostPerPerson?.value,
                        currency: _hotelDetails?.price?.averageCostPerPerson?.currency,
                    },
                    address:
                        hotel?.location?.address?.city +
                        ", " +
                        hotel?.location?.address?.country,
                    objHotel: hotel,
                    hotelAmenities: combinedAmenities,
                    guidForBooking: params.GUID,
                    flightOffer,
                    totalTravelers
                };

                dispatch(setHotelBooking(dataToAdd));
                dispatch(setSelectedHotel(dataToAdd));
                dispatch(setSelectedHotelDetail(roomsTypes));
                dispatch(setHotelRoom(roomsTypes[0]));
                replaceQueryParam('roomKey', roomsTypes[0]?.roomKey);

                if (searchfilters.roomFlights?.length && getQueryParamsFromURL()?.fav === "true") {
                    const _flightOffer = { ...searchfilters.roomFlights[0] };
                    _flightOffer["price"] = _hotelDetails?.price;
                    dispatch(setFlight(_flightOffer));
                }

                if (isChatOffer && flightOffer) {
                    const _flightOffer = { ...dataToAdd.flightOffer };
                    _flightOffer["price"] = _hotelDetails?.price;
                    dispatch(setRoomFlights([_flightOffer]));
                    dispatch(setFlight(_flightOffer));
                } else if (flightOffer) {
                    dispatch(setRoomFlights([flightOffer]));
                    dispatch(setFlight(flightOffer));
                }

                // First successful package details response: swap the address bar
                // for the share URL. Redux dispatches are synchronous, so the flight
                // set just above is already in the store and lands in the token.
                // The effect above keeps it in step from here on.
                syncShareUrlToAddressBar();

                if (!hasOfferAnimationPlayed()) {
                    saveOfferAnimationPlayed();

                    scrollToDetail = setTimeout(() => {
                        smoothScrollTo(flightRef.current, 3500);
                    }, 2000); // first scroll 2s after API (3.5s duration)

                    scrollToHeader = setTimeout(() => {
                        smoothScrollTo(headerRef.current, 3500);
                    }, 6500); // second scroll 3.5s after first started

                    // Fire API after both scrolls finish to avoid layout-shift
                    scrollToCombinedDetails = setTimeout(() => {
                        getHotelDetailsCombinedTrue();
                    }, 9500);
                } else {
                    getHotelDetailsCombinedTrue();
                }
            })
            .catch((error) => {
                if (axios.isCancel(error)) return;
                dispatch(setActivityIndicator(false));
                navigate(prevUrl, { replace: true });
            });
    };

    const getHotelDetailsCombinedTrue = () => {
        const params = getQueryParamsFromURL();
        params["Combined"] = true;
        delete params.fav;
        delete params.chatOffer;

        GetHotelDetailsService(params)
            .then((_hotelDetails) => {
                // console.log({_hotelDetails}, 'true');
                let total = 0;
                const objUrl = new URL(_hotelDetails?.packageDetails?.hotelOffer?.hotels[0]?.roomTypes[0]?.links?.webDetails?.href);
                if (objUrl) {
                    const sp = new URLSearchParams(objUrl.search);
                    const adultsCount = sp.get("NumAdult")?.split("|").reduce((p, c) => p + Number(c.split(":")[1]), 0);
                    const childCount = sp.get("NumChild")?.split("|").reduce((p, c) => p + Number(c.split(":")[1]), 0);
                    if (adultsCount) total += Number(adultsCount);
                    if (childCount) total += Number(childCount);
                }

                const roomsTypes = _hotelDetails?.packageDetails?.hotelOffer?.hotels[0]?.roomTypes?.map(room => ({
                    ...room,
                    rateDifference: (Number(room?.rateDifference) / total).toFixed(2)
                }));

                const is_Focus = localStorage.getItem('hotelDetailPageFocus');
                const _selectedRoom = roomsTypes[0];
                if (is_Focus === 'true') {
                    dispatch(setSelectedHotelDetail(roomsTypes));
                    dispatch(setHotelRoom(_selectedRoom));
                    replaceQueryParam('roomKey', _selectedRoom?.roomKey);
                    setRoomsFetched(true);
                }
                getMoreFlights(_selectedRoom, roomsTypes);
            })
            .catch((error) => {
                if (axios.isCancel(error)) return;
                setRoomsFetched(true);
                dispatch(setActivityIndicator(false));
                navigate(prevUrl, { replace: true });
            });
    };

    // Itinerary carried by a shared link (Branch or web link); same guard as getFlightsParams.
    const getLinkFlightNumbers = () =>
        Boolean(flightNumbersStringFromUrl) && flightNumbersStringFromUrl !== "null" ? flightNumbersStringFromUrl : null;

    const getMoreFlights = (roomParam, roomsTypes, linkFlightNumbers = getLinkFlightNumbers()) => {
        // read the up-to-date booking state from store at call time (no subscription)
        const currentBookingState = store.getState().booking;
        // Only a Branch link (payload in branchData) gets the shared-flight handling
        // below; web links (/link?data=) keep their existing flow untouched.
        const isBranchLink = Boolean(linkFlightNumbers) && Boolean(store.getState().appconfig?.branchData);

        const params = getFlightsParams(
            roomParam,
            searchfilters,
            loginConfig,
            currentBookingState,
            appConfig,
            hotelId,
            getQueryParamsFromURL().GUID,
            getQueryParamsFromURL()?.fav === "true",
            isChatOffer,
            linkFlightNumbers,
        );

        if (searchfilters.flightCabinClass) {
            params["flightCabinClass"] = searchfilters.flightCabinClass;
        }

        GetFlightDetailsService(params)
            .then((_flightDetails) => {
                // A shared itinerary that is no longer sellable must not leave the
                // package without flights: retry once without the link params, which
                // is the regular request (mirrors the mobile app's BookTrip).
                if (isBranchLink && !_flightDetails?.offers?.length) {
                    getMoreFlights(roomParam, roomsTypes, null);
                    return;
                }
                dispatch(setActivityIndicator(false));
                const is_Focus = localStorage.getItem('hotelDetailPageFocus');
                if (is_Focus === 'true') {
                    const sortedFlightsDetails = _flightDetails?.offers;
                    // With `flightOriginator: "LINK"` the API returns the shared itinerary at index 0.
                    const isLinkFlightFirst = isBranchLink &&
                        getFlightNumbersString(sortedFlightsDetails?.[0]?.newPackagedFlightOffer?.flightOffer) === linkFlightNumbers;
                    // ?.sort((a, b) => Number(a.price.averageCostPerPerson?.value) - Number(b.price.averageCostPerPerson?.value));
                    const firstFlightPrice = Number(sortedFlightsDetails[0]?.price?.averageCostPerPerson?.value);
                    const flightsArray = sortedFlightsDetails?.map((offer, index) => {
                        const price = offer.price;
                        const links = offer.links;
                        const surcharges = (Number(offer.price.averageCostPerPerson?.value) - firstFlightPrice).toFixed(0);
                        return {
                            price,
                            surcharges,
                            links,
                            isSelected: index === 0,
                            ...offer?.newPackagedFlightOffer?.flightOffer,
                        };
                    });

                    dispatch(setRoomFlights(flightsArray));

                    const departureSegmentId = flightsArray[0]?.segmentIds[0];
                    const departureFlightSegment = flightsArray[0]?.segments?.filter((f) => f.segmentId === departureSegmentId)[0];
                    const flightLeg = departureFlightSegment?.legs[0];

                    dispatch(setFlightCabinClass(flightLeg?.cabinClass));

                    // The shared itinerary wins over the flight the fresh search pre-selected
                    // (FlightOfferId), which is not the one the sharer picked.
                    const previousSelectedFlight = !isLinkFlightFirst
                        ? flightsArray?.filter((f) => f.clientSelected && f.offerId === params.FlightOfferId)
                        : [];
                    if (previousSelectedFlight?.length) {
                        dispatch(setFlight(previousSelectedFlight[0]));
                        replaceQueryParam('offerId', previousSelectedFlight[0]?.offerId);
                    } else {
                        const _selectedFlight = flightsArray[0];
                        dispatch(setFlight(_selectedFlight));
                        replaceQueryParam('offerId', _selectedFlight?.offerId);
                    }
                    setFlightsFetched(true);
                }
            })
            .catch((error) => {
                console.log({ error });
                const _params = getQueryParamsFromURL();
                if (_params["roomKey"] !== roomParam?.roomKey) {
                    let _roomsTypes = roomsTypes?.length ? roomsTypes : searchfilters.selectedHotelDetail;
                    const _room = _roomsTypes?.find((f) => f?.roomKey === _params["roomKey"]);
                    if (_room) {
                        calculateRoomsUpSell(_room, _roomsTypes, dispatch).then((res) => {
                            console.log({ res });
                        });
                    }
                }
                dispatch(setActivityIndicator(false));
                setFlightsFetched(true);
            });
    };

    return (
        <div className="main-wrapper flight-page">
            <AppHeader />
            <main className="body-wrapper">
                <Topbar flightRef={flightRef} headerRef={headerRef} />
                <div className="container">
                    <div className="row">
                        <div className="col-lg-7">
                            <DetailImages images={images} type="hotel" />
                        </div>
                        <div className="col-lg-5">
                            <div id="detailsContent" className="details-content-wrapper sidecontent">
                                <DetailsContent />
                            </div>
                        </div>
                    </div>
                </div>

                <div id="detailsContent" className="details-content-wrapper">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-12 col-md-12">
                                <div className="col-lg-12 col-md-12">
                                    <div className="content-holder">
                                        <ul className="features-deatils row">
                                            <h6 ref={amenityHeading}>{T("Packages_Detail:Amenities")}</h6>
                                            {amenities.map((amenity, index) => (
                                                <li className="col-md-4" key={index}>
                                                    {amenity?.name}
                                                </li>
                                            ))}
                                        </ul>

                                        <button
                                            className="showmore-btn"
                                            onClick={() => setShowMore(!showMore)}
                                        >
                                            {showMore ? T("Buttons:Show_Less") : T("Buttons:Show_More")}
                                        </button>

                                        <div className="steps-box mobile-steps">
                                            <ProgressBar
                                                percent={calculatePercentage()}
                                                height={3}
                                                filledBackground="linear-gradient(to right, #007bff, #007bff)"
                                                className="my-progress"
                                            >
                                                {steps?.map((step, index) => (
                                                    <Step key={index} index={index} transition="scale">
                                                        {() => (
                                                            <div className={"step"}>
                                                                <div
                                                                    className={`circle ${step?.completed ? "active" : ""}`}>
                                                                    <img className={"step-icon"} src={step?.icon}
                                                                        alt={"step-icon"} />
                                                                </div>
                                                                <p className={"step-label"}>{step?.name}</p>
                                                            </div>
                                                        )}
                                                    </Step>
                                                ))}
                                            </ProgressBar>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-4 col-md-6 forSmaller" ref={bookingCardMobile}>
                                    {room && flight ? <DetailCard steps={steps} setSteps={setSteps} roomsFetched={roomsFetched} flightsFetched={flightsFetched} /> : null}
                                </div>

                            </div>
                            <div className="col-lg-4 col-md-6 forDesktop" ref={bookingCardDesktop}>
                                {room && flight ? <DetailCard steps={steps} setSteps={setSteps} roomsFetched={roomsFetched} flightsFetched={flightsFetched} /> : null}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="container double_section">
                    <div className="row">
                        <div id={"rooms-section"} className="col-lg-6">
                            <RoomDetails roomsFetched={roomsFetched} />
                        </div>
                        <div ref={flightRef} id={"flights-section"} className="col-lg-6">
                            <FlightDetails flightsFetched={flightsFetched} />
                        </div>
                    </div>
                </div>

                {/*<TextSection/>*/}
            </main>
            <Footer />
        </div>
    );
};

export default Deatils;
